import { Controller } from "stimulus";
import { jitter, expBackoff } from "live_chat/backoff";
import { whenPlaybackStops, CROWD_REFRESH_SPREAD_MS } from "utils/live_playback";

// The server renders its timestamps in whole seconds, so a handoff that went at the exact moment
// could reach it a fraction early, resolve the same event, and reload straight back.
const HANDOFF_MARGIN_MS = 2000;
// Spread over which the audience waiting on a stream reloads just its stage frame at go-live. Shorter
// than the crowd spread because a stage request is far cheaper than a full page render.
const STAGE_REFRESH_SPREAD_MS = 15000;
// Frame reloads tried (with backoff) before one spread full refresh takes over.
const STAGE_MAX_ATTEMPTS = 6;
// Spread over which a premiere's audience reloads its stage frame once the preload lead begins, to
// mount the held player. Well inside LiveEvent::PREMIERE_PRELOAD_LEAD so every viewer has time to buffer.
const PREMIERE_PRELOAD_SPREAD_MS = 60000;
// Spread over which viewers still playing a finished premiere reload once they stop it.
const PLAYBACK_STOP_SPREAD_MS = 5000;

// Waiting-room countdown -> reveal a "chat closes in" countdown once the content finishes;
// transition the room to its closed state in place (no navigation) once a premiere's or stream's
// room closes - the video keeps playing to the end, the viewer is already entitled to the VOD.
// Shared by both live streams and premieres now. endsAtMs/playbackEndsAtMs are real for either
// kind: a stream's chat runs on for its own afterparty past the moment the creator ended it.
// At T-0 the two kinds diverge (see renderCountdown): a premiere is clock-driven, so reaching
// the target reloads into the playing state; a stream is webhook-driven, so reaching starts_at
// says nothing about whether the creator has actually gone live - kindValue picks the branch.
// waitingValue is the ticking-countdown gate, not the same thing as the page's own "waiting room"
// content state: a deferred premiere or a stream stuck past its own starts_at also render
// waiting-room copy, but with no live target to count down to, so the view leaves waitingValue
// false for those - see HeroComponent#countdown_target_ms.
// /live resolves the event itself, so once this event's content has finished the page hands off
// to the next one by reloading - see watchHandoff.
export default class ShowController extends Controller {
  static targets = ["countdown", "countdownLabel", "countdownOverdue", "chatClose", "chatCloseBar", "nextUp", "refreshStream", "stage", "cover", "heldPlayer", "feedbackPrompt"];
  static values = {
    kind: String,
    startsAtMs: Number,
    endsAtMs: Number,
    serverNowMs: Number,
    playbackEndsAtMs: Number,
    waiting: Boolean,
    // Seeded only when the page rendered before its chat room opened, and only with a future moment.
    roomOpensAtMs: Number,
    // Seeded only for a premiere viewer waiting outside the preload lead: when the held player should mount.
    preloadAtMs: Number,
    // When the next event's pre-show opens; seeded only while there is one.
    nextOpensAtMs: Number,
  };

  connect() {
    // Offset a skewed client clock onto server time (minted at render).
    this.clockOffset = this.serverNowMsValue - Date.now();

    if (this.waitingValue && this.startsAtMsValue <= this.now()) {
      // Deferred one frame, as in watchRoomClose: the held player's window action is bound only once the whole tree has connected.
      this.countdownFrame = window.requestAnimationFrame(() => this.renderCountdown());
    } else if (this.waitingValue) {
      this.renderCountdown();
      this.countdownInterval = window.setInterval(() => this.renderCountdown(), 1000);
    }

    this.watchPreload();
    this.watchAfterparty();
    this.watchRoomClose();
    this.watchRoomOpen();
    this.watchHandoff();
  }

  disconnect() {
    if (this.countdownInterval) window.clearInterval(this.countdownInterval);
    if (this.countdownFrame) window.cancelAnimationFrame(this.countdownFrame);
    if (this.windowEndInterval) window.clearInterval(this.windowEndInterval);
    if (this.afterpartyInterval) window.clearInterval(this.afterpartyInterval);
    if (this.closeRoomFrame) window.cancelAnimationFrame(this.closeRoomFrame);
    if (this.roomOpenInterval) window.clearInterval(this.roomOpenInterval);
    if (this.handoffInterval) window.clearInterval(this.handoffInterval);
    if (this.handoffTimer) window.clearTimeout(this.handoffTimer);
    if (this.cancelPlaybackWait) this.cancelPlaybackWait();
    if (this.stageTimer) window.clearTimeout(this.stageTimer);
    this.cancelPremiereReloads();
  }

  now() {
    return Date.now() + this.clockOffset;
  }

  renderCountdown() {
    const remaining = this.startsAtMsValue - this.now();
    if (remaining <= 0) {
      if (this.countdownInterval) window.clearInterval(this.countdownInterval);

      if (this.kindValue === "premiere") {
        // The player was mounted early behind the countdown: starting it is purely client-side, so
        // everyone begins on the dot with no request.
        if (this.startHeldPlayer()) return;

        // No startable held player (preload missed, native shell, or a placeholder): clock-driven, so
        // reload into the playing state (fresh signed URL + player). Spread like every other crowd-wide
        // reload: a late joiner lands on the shared playhead anyway.
        this.scheduleFallbackReload();
        return;
      }

      // Webhook-driven: reaching starts_at does not mean the creator has actually gone live
      // yet - mark_live! re-renders this page whenever it really happens (the chat room's
      // stream_started event, or the live_stream_settings stream while the room is not open
      // yet). Swap to the same static "Starting soon" markup a fresh
      // load in this state would render, rather than reload-looping on a stale target.
      if (this.hasCountdownLabelTarget) this.countdownLabelTarget.classList.add("hidden");
      if (this.hasCountdownTarget) this.countdownTarget.classList.add("hidden");
      if (this.hasCountdownOverdueTarget) this.countdownOverdueTarget.classList.remove("hidden");
      return;
    }
    if (this.hasCountdownTarget) this.countdownTarget.textContent = this.format(remaining);
  }

  // Pending reloads that must not outlive a premiere that has started playing.
  cancelPremiereReloads() {
    if (this.preloadInterval) window.clearInterval(this.preloadInterval);
    if (this.premiereTimer) window.clearTimeout(this.premiereTimer);
    if (this.cancelReloadWait) this.cancelReloadWait();
    if (this.stageTimer) window.clearTimeout(this.stageTimer);
    this.preloadInterval = this.premiereTimer = this.cancelReloadWait = this.stageTimer = null;
  }

  // The T-0 full reload for a premiere with no held player to start; a stage that lands meanwhile cancels it.
  scheduleFallbackReload() {
    // The stage preload is moot now: this reload replaces the whole page.
    if (this.preloadInterval) window.clearInterval(this.preloadInterval);
    this.preloadInterval = null;
    if (this.premiereTimer) return;

    this.premiereTimer = window.setTimeout(() => {
      this.premiereTimer = null;
      this.cancelReloadWait = whenPlaybackStops(() => {
        this.cancelReloadWait = null;
        window.location.reload();
      });
    }, jitter(0, CROWD_REFRESH_SPREAD_MS));
  }

  // Reveal the "chat closes in M:SS" line and count it down to the afterparty end - both when the page
  // loads mid-afterparty and when a premiere's video finishes while the viewer is watching (no reload).
  watchAfterparty() {
    if (!this.playbackEndsAtMsValue || !this.hasChatCloseBarTarget) return;

    const tick = () => {
      if (this.now() < this.playbackEndsAtMsValue) return;
      this.chatCloseBarTarget.classList.remove("hidden");
      if (this.hasChatCloseTarget) {
        this.chatCloseTarget.textContent = this.format(Math.max(0, this.endsAtMsValue - this.now()));
      }
    };

    tick();
    this.afterpartyInterval = window.setInterval(tick, 1000);
  }

  // Room close can be hours out; setTimeout is unreliable over long spans/sleep, so poll the offset clock.
  watchRoomClose() {
    if (!this.endsAtMsValue) return;

    if (this.now() >= this.endsAtMsValue) {
      // Deferred one rAF tick, not called inline: this controller is an ancestor of the chat
      // panel, so Stimulus connects it first - dispatching synchronously here fires before the
      // panel's window listener exists. One frame is always enough because Stimulus finishes
      // connecting the whole tree, descendants' data-actions included, within the current
      // task/mutation-observer checkpoint.
      this.closeRoomFrame = window.requestAnimationFrame(() => this.closeRoom());
      return;
    }

    this.windowEndInterval = window.setInterval(() => {
      if (this.now() >= this.endsAtMsValue) {
        window.clearInterval(this.windowEndInterval);
        this.closeRoom();
      }
    }, 1000);
  }

  // Nothing re-renders a page when its room opens (chat was refused, and the later go-live refresh keeps the
  // turbo-permanent chat panel's pre-open markup), so reload once it opens: a full reload. No loop is possible
  // because the server seeds only a future moment.
  watchRoomOpen() {
    if (!this.roomOpensAtMsValue) return;

    const reloadAtMs = this.roomOpensAtMsValue + Math.random() * CROWD_REFRESH_SPREAD_MS;
    this.roomOpenInterval = window.setInterval(() => {
      if (this.now() < reloadAtMs) return;
      window.clearInterval(this.roomOpenInterval);
      window.location.reload();
    }, 1000);
  }

  // A premiere's player is mounted minutes before T-0 (the server renders it held behind the countdown
  // once inside LiveEvent::PREMIERE_PRELOAD_LEAD) so the video is already loading when it must start.
  // A page opened earlier reloads just its stage frame when the lead begins, spread so the crowd does
  // not arrive at once. If that never lands, T-0 falls back to the spread reload in renderCountdown.
  // Polled rather than timed out: the lead can be most of an hour away and setTimeout is unreliable across sleep.
  watchPreload() {
    if (!this.preloadAtMsValue || !this.hasStageTarget) return;

    this.stageAttempt = 0;
    const reloadAtMs = this.preloadAtMsValue + jitter(0, PREMIERE_PRELOAD_SPREAD_MS);
    this.preloadInterval = window.setInterval(() => {
      if (this.now() < reloadAtMs) return;

      window.clearInterval(this.preloadInterval);
      this.preloadInterval = null;
      this.reloadStage();
    }, 1000);
  }

  // Reveal the held player and tell it to start; false when there is no loaded player to start.
  startHeldPlayer() {
    if (!this.hasHeldPlayerTarget || !this.heldPlayerReady()) return false;

    this.cancelPremiereReloads();
    this.heldPlayerTarget.removeAttribute("inert");
    this.coverTargets.forEach((layer) => layer.classList.add("hidden"));
    this.revealFeedbackPrompt();
    this.dispatch("premiere-start", { target: window });
    return true;
  }

  // A connected, unfailed video player; the "Coming soon" placeholder (a video still processing) has none.
  heldPlayerReady() {
    const element = this.heldPlayerTarget.querySelector('[data-controller~="media--video-player"]');
    const player = element && this.application.getControllerForElementAndIdentifier(element, "media--video-player");
    return Boolean(player?.video && !player.video.error);
  }

  // The playback feedback prompt rides in a template while the player is held; there is something to rate once it plays.
  revealFeedbackPrompt() {
    if (!this.hasFeedbackPromptTarget) return;

    const template = this.feedbackPromptTarget;
    template.after(template.content.cloneNode(true));
    template.remove();
  }

  // After a stage swap: refill the countdown the new hero renders empty, and settle the T-0 handling
  // for a stage that landed late (start its held player, or stop a reload that would pull a playing video away).
  stageLoaded(event) {
    if (!this.isStage(event) || !this.waitingValue) return;

    if (this.kindValue === "premiere" && this.now() >= this.startsAtMsValue) {
      if (this.startHeldPlayer()) return;
      if (!this.hasHeldPlayerTarget && this.stageTarget.querySelector("video, hlsjs-video")) this.cancelPremiereReloads();
      return;
    }

    const remaining = this.startsAtMsValue - this.now();
    if (remaining > 0 && this.hasCountdownTarget) this.countdownTarget.textContent = this.format(remaining);
  }

  // A stream going live while this page waits only changes the stage (waiting box -> player), so
  // reload just that frame from a lean endpoint, spread across the audience, instead of re-rendering
  // the whole page. Cancelling the event tells the chat panel not to fall back to a full refresh.
  // Not handled (so the full refresh runs): a premiere (clock-driven), or no stage frame on the page.
  goLive(event) {
    if (this.kindValue !== "stream" || !this.hasStageTarget) return;

    event.preventDefault();
    // Already playing, or a reload already scheduled: nothing more to do.
    if (this.stageTimer || this.stageTarget.querySelector("video, hlsjs-video")) return;

    this.stageAttempt = 0;
    this.stageTimer = window.setTimeout(() => this.reloadStage(), jitter(0, STAGE_REFRESH_SPREAD_MS));
  }

  reloadStage() {
    this.stageTimer = null;
    if (!this.hasStageTarget) return;

    const stage = this.stageTarget;
    // Setting src loads the frame (Turbo adds the Turbo-Frame header); once set, reload() re-fetches it.
    if (stage.src) stage.reload();
    else stage.src = window.location.href;
  }

  // The response did not hold this stage frame: another event now wins /live, so take the page it
  // answered with as a normal visit (no second request). An error page is a failed fetch instead.
  stageMissing(event) {
    if (!this.isStage(event)) return;

    // Ours alone: the global frame_missing_handler would re-fetch the page or paint an error over the hero.
    event.preventDefault();
    event.stopPropagation();
    const { response } = event.detail;
    if (response.ok) {
      // The native shell routes a visit through its adapter, which cannot reuse the response and
      // would push a second screen, so it refreshes in place instead.
      if (document.querySelector("[data-hotwire-native]")) window.Turbo.session.refresh(window.location.href);
      else event.detail.visit(response);
    }
    else if (response.status >= 500) this.retryStage();
    else this.refreshFully();
  }

  stageFailed(event) {
    if (this.isStage(event)) this.retryStage();
  }

  // Turbo dispatches neither of the events above for an error that is not HTML (a proxy's or Puma's
  // text/plain 5xx), so catch it here.
  stageResponse(event) {
    if (!this.isStage(event)) return;

    const { fetchResponse } = event.detail;
    if (!fetchResponse.succeeded && !fetchResponse.isHTML) this.retryStage();
  }

  // Back off and retry the frame rather than refresh the page: a full refresh would add load at the
  // worst moment. After STAGE_MAX_ATTEMPTS the frame is not coming (a persistent error), so one
  // spread full refresh takes over.
  retryStage() {
    if (this.stageTimer) return;
    if (this.stageAttempt >= STAGE_MAX_ATTEMPTS) {
      this.refreshFully();
      return;
    }

    this.stageTimer = window.setTimeout(() => this.reloadStage(), expBackoff(this.stageAttempt++));
  }

  refreshFully() {
    if (this.stageTimer) return;

    this.stageTimer = window.setTimeout(() => window.Turbo.session.refresh(window.location.href), jitter(0, CROWD_REFRESH_SPREAD_MS));
  }

  isStage(event) {
    return this.hasStageTarget && event.target === this.stageTarget;
  }

  // Once the content has finished, reveal "Next up" and reload into the next event: as soon as its
  // pre-show is open (right away if it already is), spread across the audience. A live stream has
  // no playbackEndsAtMs yet; its stream_ended refresh re-renders the page with one. Polled rather
  // than timed out, since the next pre-show can be hours away and setTimeout is unreliable across sleep.
  watchHandoff() {
    if (!this.nextOpensAtMsValue) return;

    const spreadMs = jitter(0, CROWD_REFRESH_SPREAD_MS);
    const tick = () => {
      if (!this.playbackEndsAtMsValue || this.now() < this.playbackEndsAtMsValue) return;

      if (this.hasNextUpTarget) this.nextUpTarget.classList.remove("hidden");
      if (this.now() < Math.max(this.playbackEndsAtMsValue, this.nextOpensAtMsValue) + HANDOFF_MARGIN_MS + spreadMs) return;

      window.clearInterval(this.handoffInterval);
      this.handoffInterval = null;
      this.handOffWhenStopped();
    };

    this.handoffInterval = window.setInterval(tick, 1000);
    tick();
  }

  // Never refresh a viewer away from a video still playing: a premiere viewer who rewound watches it
  // to the end (or pauses) first, and a native-shell viewer's bridge player keeps the page for good.
  handOffWhenStopped() {
    this.cancelPlaybackWait = whenPlaybackStops(({ waited }) => {
      this.cancelPlaybackWait = null;
      this.handOff(waited ? jitter(0, PLAYBACK_STOP_SPREAD_MS) : 0);
    });
  }

  handOff(delayMs) {
    if (this.handedOff) return;

    this.handedOff = true;
    this.handoffTimer = window.setTimeout(() => window.location.reload(), delayMs);
  }

  // In-place transition once the room closes - no redirect, the video keeps playing where it is.
  // The chat panel's own "Chat is closed." notice is the one place that says so; this bar just
  // stops counting down rather than restating it.
  // The page then holds neither a room nor, having rendered with one open, a cable stream: it
  // subscribes to the refresh stream now, so what happens after the afterparty still reaches it.
  closeRoom() {
    if (this.afterpartyInterval) window.clearInterval(this.afterpartyInterval);
    if (this.hasChatCloseBarTarget) this.chatCloseBarTarget.classList.add("hidden");
    this.dispatch("room-closed", { target: window });
    if (this.hasRefreshStreamTarget) this.element.append(this.refreshStreamTarget.content.cloneNode(true));
  }

  // MM:SS, widening to H:MM:SS once an hour or more remains, and to "2d 03:04:05" for an event days out.
  format(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (days > 0) return `${days}d ${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`;
    if (hours > 0) return `${hours}:${this.pad(minutes)}:${this.pad(seconds)}`;
    return `${this.pad(minutes)}:${this.pad(seconds)}`;
  }

  pad(value) {
    return String(value).padStart(2, "0");
  }
}
